/* eslint-disable */
import { usePropsStore } from "@/stores/props";
import { Log, UserManager, WebStorageStateStore, type UserManagerSettings } from "oidc-client-ts";

let settings: UserManagerSettings = {
  // Where the tokens will be stored
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  // URL to the authentication server (including realm)
  authority: "",
  // Client id value found in idp application settings
  client_id: "",
  // Where to redirect the user to after successful authentication
  redirect_uri: window.location.origin + "/login",
  // Indicate the the authorization code flow should be used
  response_type: "code",
  // "openid" tells the server that this client uses oidc for authentication
  scope: "openid profile email",
  // Disable the automatic renewal
  automaticSilentRenew: false,
  prompt: "login",
};

let userManager: UserManager | null = null;

Log.setLogger(console);
Log.setLevel(Log.INFO);

export class SSOService {
  createUserManager(): UserManager {
    const propsStore = usePropsStore();
    settings.authority = propsStore.ssoDomain;
    settings.client_id = propsStore.ssoClientId;
    if (propsStore.ssoIssuer.length > 0) {
      const metadata = {
        issuer: propsStore.ssoIssuer,
        jwks_uri: propsStore.ssoJwksUri,
        end_session_endpoint: propsStore.ssoEndSessionEndpoint,
        authorization_endpoint: propsStore.ssoAuthorizationEnpoint,
        token_endpoint: propsStore.ssoTokenEndpoint,
      };
      settings.metadata = metadata;
    }
    return new UserManager(settings);
  }

  login() {
    if (userManager == null) userManager = this.createUserManager();
    userManager.signinRedirect().catch(function (err) {
      console.log(err);
    });
  }

  logout() {
    if (userManager == null) userManager = this.createUserManager();
    userManager
      .signoutRedirect()
      .then(() => console.log("User logged out"))
      .catch((error) => console.log(error));
  }

  /**
   * Handles the redirect from the OAuth server after a user logged in.
   */
  async handleLoginRedirect() {
    if (userManager == null) userManager = this.createUserManager();
    return userManager.signinRedirectCallback();
  }

  /**
   * Handles the redirect from the OAuth server after a user logged out.
   */
  handleLogoutRedirect() {
    if (userManager == null) userManager = this.createUserManager();
    return userManager.signoutRedirectCallback();
  }

  /**
   * Checks whether or not a user is currently logged in.
   * Returns a promise which will be resolved to true/false or be rejected with an error.
   */
  isUserLoggedIn() {
    return new Promise((resolve, reject) => {
      if (userManager == null) userManager = this.createUserManager();
      userManager
        .getUser()
        .then((user) => {
          if (user == null) resolve(false);
          else resolve(true);
        })
        .catch((error) => reject(error));
      // }
    });
  }

  /**
   * Get the profile data for the currently authenticated user.
   * Returns an empty object if no user is logged in.
   */
  getProfile() {
    return new Promise((resolve, reject) => {
      if (userManager == null) userManager = this.createUserManager();
      userManager
        .getUser()
        .then((user) => {
          console.log(user);
          if (user == null) resolve(null);
          else resolve(user.profile);
        })
        .catch((error) => reject(error));
      // }
    });
  }

  /**
   * Get the access token.
   * Not really used right now
   */
  getAccessToken() {
    return new Promise((resolve, reject) => {
      if (userManager == null) userManager = this.createUserManager();
      console.log("Getting access token from user");
      userManager
        .getUser()
        .then((user) => {
          if (user == null) reject("User not found");
          else resolve(user.access_token);
        })
        .catch((error) => reject(error));
      // }
    });
  }

  /**
   * Get the ID token.
   * Can be used to log in and receive the api's jwt back
   */
  getIDToken() {
    return new Promise((resolve, reject) => {
      if (userManager == null) userManager = this.createUserManager();
      console.log("Getting ID token from user");
      userManager
        .getUser()
        .then((user) => {
          if (user == null) reject("User not found");
          else resolve(user.id_token);
        })
        .catch((error) => reject(error));
    });
  }
}

export const sso = new SSOService();
